import React from "react";
import { Link } from "react-router-dom";
import {
  InformationCircleIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  LockClosedIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Section from "../components/marketing/Section";
import SectionHeading from "../components/marketing/SectionHeading";
import FeatureCard from "../components/marketing/FeatureCard";
import CTAButton from "../components/marketing/CTAButton";

/**
 * /security — every claim here is verified against the actual GitHub App
 * configuration and auth-callback implementation (see plan file's item 4
 * sub-task A, and CLAUDE.md's Auth Flow Summary), not paraphrase:
 *   - Permissions: Repository -> Metadata: Read (mandatory), Pull requests:
 *     Read, Contents: Read. Nothing else is requested.
 *   - "No GitHub token is ever stored" is CLAUDE.md's own verbatim claim —
 *     not stored server-side, not in localStorage. The user-to-server token
 *     from OAuth sign-in is used once, in-request, during auth-callback, and
 *     discarded (never persisted, per the GitHub App auth model).
 *   - Installation tokens: minted on demand, expire in 1 hour, belong to the
 *     installation rather than to any individual.
 *
 * Item 24 audited this page against the code again and found three claims
 * that had drifted past what's true. All three are corrected inline below,
 * with the reasoning at each site. The lesson worth keeping: "verified
 * against the implementation" decays as the implementation changes, so any
 * claim here should be re-checked whenever data handling does — and must
 * stay consistent with /privacy, which now enumerates the same facts.
 */
const PERMISSIONS = [
  {
    icon: InformationCircleIcon,
    title: "Metadata",
    body: "Basic repository info — required by GitHub for any App installation.",
  },
  {
    icon: DocumentTextIcon,
    title: "Pull requests",
    // Corrected item 24: previously said "and diffs". GitHub's files API
    // does return each file's diff, but pr-analyzer reads only filename,
    // additions and deletions from that response and discards the rest —
    // no diff is ever stored. Claiming diffs overstated what's kept.
    //
    // Updated again for the review write-back: this is the only permission
    // that is not read-only, so it has to say so plainly rather than let a
    // visitor infer read-only from the surrounding cards. The write is
    // exactly one API call — POST/DELETE requested_reviewers.
    body: "Titles, changed file paths, and line counts — used to calculate complexity and route reviewers. This is also the one permission that lets Anthurium write: it requests the reviewer it picked, and nothing else.",
  },
  {
    icon: FolderOpenIcon,
    title: "Contents",
    // Corrected item 24: previously said "File contents... used to detect
    // critical paths". No file body is ever read — critical-path detection
    // is a regex over filenames (CRITICAL_PATH_PATTERNS). The permission is
    // genuinely needed, but for the commits API the expertise backfill uses.
    body: "Granted read-only as part of the App's installation. Anthurium uses it only to see which files changed — never their contents.",
  },
];

export default function SecurityPage() {
  return (
    <div className="overflow-x-hidden bg-rf-bg-primary">
      <MarketingNav />

      <Section className="!pb-16">
        <SectionHeading
          eyebrow="Security"
          title="Your code never leaves your repo"
          lead="Anthurium scores and routes pull requests from file paths and line counts alone — it never reads the contents of a diff, never stores your source, and never sends any of it to a model. It never writes to your code, and it never asks for more access than that."
        />
      </Section>

      <Section variant="elevated" className="!pt-12 md:!pt-16">
        <h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-rf-text-primary">
          What Anthurium can access
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {PERMISSIONS.map((item) => (
            <FeatureCard key={item.title} icon={item.icon} title={item.title}>
              {item.body}
            </FeatureCard>
          ))}
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-[68ch]">
          {/* Corrected item 24: this heading used to read "Nothing is stored
              long-term". Both paragraphs below it are about *credentials*
              and are accurate, but the heading generalised that to all data
              — the opposite of the truth, since PR titles, file paths and
              line counts are retained for as long as the team uses
              Anthurium. Scoped to credentials, with the third paragraph
              stating what actually is kept and linking to the full list. */}
          <h2 className="mb-4 text-2xl font-bold tracking-tight text-rf-text-primary">
            No credentials are stored
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-rf-text-secondary">
            Anthurium authenticates as a GitHub App installation, not as any
            individual person. When it needs to read a pull request, it mints
            a short-lived installation token directly from GitHub — valid for
            one hour, and tied to the installation, not to any teammate's
            personal account.
          </p>
          <p className="mb-4 text-sm leading-relaxed text-rf-text-secondary">
            Your GitHub sign-in is used only to prove who you are when you log
            in. That credential is used once, in a single request, and never
            saved — not in our database, not in your browser. There's nothing
            long-lived sitting around to leak.
          </p>
          <p className="text-sm leading-relaxed text-rf-text-secondary">
            Pull request metadata — titles, changed file paths, and line
            counts — is stored for as long as your team uses Anthurium, and
            deleted when you delete your account. Your source code never is.
            See our{" "}
            <Link to="/privacy" className="text-rf-accent hover:underline">
              privacy policy
            </Link>{" "}
            for the full list of what's kept and for how long.
          </p>
        </div>
      </Section>

      <Section variant="elevated">
        <div className="grid gap-8 sm:grid-cols-2">
          <div className="flex items-start gap-4">
            <LockClosedIcon className="h-6 w-6 shrink-0 text-rf-accent" aria-hidden="true" />
            <div>
              <h3 className="mb-1 text-base font-semibold text-rf-text-primary">
                Never writes to your code
              </h3>
              {/*
                Corrected when the review write-back shipped. This previously
                read "Every permission requested is read-only", which stopped
                being true the moment the App gained Pull requests: Write to
                request reviewers. The heading above it survives unchanged and
                is worth keeping precise about: Pull requests: Write allows
                requesting a reviewer and commenting — it does NOT allow
                pushing commits or modifying files, which is a separate
                Contents: Write permission the App does not hold. Do not widen
                the App's permissions without revisiting this paragraph.
              */}
              <p className="text-sm leading-relaxed text-rf-text-secondary">
                Anthurium can request a reviewer on a pull request — that's
                the one thing it writes, and it's how the person it picks finds
                out. It can't push a commit, change a file, or merge anything:
                those need permissions the App doesn't hold and never asks for.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ArrowUturnLeftIcon className="h-6 w-6 shrink-0 text-rf-accent" aria-hidden="true" />
            <div>
              <h3 className="mb-1 text-base font-semibold text-rf-text-primary">
                Uninstall anytime
              </h3>
              <p className="text-sm leading-relaxed text-rf-text-secondary">
                Manage or remove access from your GitHub organization's
                settings at any time — no support ticket required, nothing to
                ask us for.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section variant="elevated" className="!pt-0">
        <div className="flex flex-col items-center gap-6 text-center">
          <CTAButton to="/signup">Start free trial</CTAButton>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
